import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import AppIcon from '../components/AppIcon';
import Input from '../components/Input';
import Button from '../components/Button';
import SupportContact from '../components/SupportContact';

const Login: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [loginError, setLoginError] = useState('');

  const validate = () => {
    const newErrors: { email?: string; password?: string } = {};
    if (!email) newErrors.email = t('errors.required');
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = t('errors.invalidEmail');
    if (!password) newErrors.password = t('errors.required');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoginError('');
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Uspješna prijava!');
      // App routes the authenticated account to its role-specific home.
    } catch (error: any) {
      // Check for network error (no response from server)
      if (!error.response) {
        const message = t('auth.loginNetworkError');
        setLoginError(message);
        toast.error(message, { autoClose: 8000 });
        setLoading(false);
        return;
      }
      
      // Handle specific error cases with clear messages
      if (error.response?.data?.mustResetPassword) {
        const message = t('auth.mustResetPassword');
        setLoginError(message);
        toast.warning(message, { autoClose: 10000 });
        setLoading(false);
        return;
      }
      
      if (error.response?.data?.requiresVerification) {
        const message = t('auth.verifyBeforeLogin');
        setLoginError(message);
        toast.warning(message);
        navigate('/verify-email', { state: { email } });
        setLoading(false);
        return;
      }
      
      if (error.response?.data?.locked) {
        const mins = error.response?.data?.remainingMinutes || 30;
        const message = t('auth.accountLocked', { minutes: mins });
        setLoginError(message);
        toast.error(message);
        setLoading(false);
        return;
      }
      
      // Default error message
      const message = error.response?.status === 401
        ? t('auth.invalidCredentials')
        : error.response?.data?.message || t('errors.somethingWrong');
      setLoginError(message);
      toast.error(message);
      setLoading(false);
    }
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'hr' ? 'en' : 'hr';
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  return (
    <main id="main-content" tabIndex={-1} className="login-page min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mb-4">
              <img src="/zoma-logo.svg?v=6" alt="ZoMa" width={80} height={80} style={{ display: 'block', margin: '0 auto' }} />
            </div>
            <h1 className="text-3xl font-bold text-gray-800 dark:text-white">ZoMa</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('app.poweredBy')}</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {loginError && (
              <div
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-200"
              >
                {loginError}
              </div>
            )}

            <Input
              type="email"
              label={t('auth.email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={errors.email}
              placeholder="email@example.com"
              autoComplete="email"
            />

            <Input
              type={showPassword ? "text" : "password"}
              label={t('auth.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={errors.password}
              placeholder="••••••••"
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-sm text-gray-500 hover:text-primary mt-1 flex items-center gap-1"
              style={{ border: 0, background: 'transparent', padding: '4px 0', cursor: 'pointer' }}
            >
              <AppIcon name={showPassword ? 'eye-off' : 'eye'} size={16} />
              {showPassword ? "Sakrij lozinku" : "Prikaži lozinku"}
            </button>

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-primary hover:underline"
              >
                {t('auth.forgotPassword')}
              </Link>
            </div>

            <Button type="submit" className="w-full" loading={loading}>
              {t('auth.login')}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-4 text-center"><SupportContact /></div>
          {/* Language toggle */}
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={toggleLanguage}
              className="text-sm text-gray-500 hover:text-primary"
            >
              {i18n.language === 'hr' ? 'Engleski' : 'Hrvatski'}
            </button>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Login;
